import React, { useEffect, useState } from 'react';
import { FileText, Send } from 'lucide-react';
import MediaMessageControls from './MediaMessageControls';

export default function MessageInput({
  onSend,
  disabled = false,
  placeholder = 'Escribe un mensaje interno...',
  allowNotes = true,
  allowMedia = true,
  helperText = 'WhatsApp se registra aquí, pero este campo envía mensajes internos.',
}) {
  const [content, setContent] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const mediaEnabled = allowMedia && !isNote;

  useEffect(() => {
    if (!allowNotes) setIsNote(false);
  }, [allowNotes]);

  async function handleSubmit(event) {
    event.preventDefault();
    const text = content.trim();
    if ((!text && !mediaAttachment) || disabled || isSending) return;
    setIsSending(true);
    try {
      const shouldSendNote = allowNotes && isNote;
      await onSend?.({
        content: text,
        is_note: shouldSendNote,
        message_type: mediaAttachment?.type || (shouldSendNote ? 'note' : 'text'),
        media_file: mediaAttachment?.file,
        media_type: mediaAttachment?.type,
      });
      setContent('');
      setMediaAttachment(null);
      setIsNote(false);
    } catch {
      // ConversationThread displays the send error; keep the draft for retry.
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if ((!content.trim() && !mediaAttachment) || disabled || isSending) return;
    handleSubmit(event);
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-slate-200 bg-white p-2 sm:p-3">
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        {allowNotes && (
          <button
            type="button"
            onClick={() => {
              setIsNote(prev => !prev);
              setMediaAttachment(null);
            }}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold sm:w-auto sm:py-1.5 ${
              isNote ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Nota interna
          </button>
        )}
        <span className="min-w-0 break-words text-[11px] leading-snug text-slate-500">{helperText}</span>
      </div>

      {mediaEnabled && (
        <div className="mb-2">
          <MediaMessageControls
            value={mediaAttachment}
            onChange={setMediaAttachment}
            disabled={disabled || isSending}
            compact
          />
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <textarea
          value={content}
          onChange={event => setContent(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isSending}
          rows={2}
          placeholder={placeholder}
          className="min-h-[76px] flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20 font-montserrat"
        />
        <button
          type="submit"
          disabled={disabled || isSending || (!content.trim() && !mediaAttachment)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#B1336E] px-4 py-3 text-sm font-bold text-white hover:bg-[#9d2d62] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:self-end sm:py-2"
        >
          <Send className="w-4 h-4" />
          Enviar
        </button>
      </div>
    </form>
  );
}
